import { isEmpty, isNil, omitBy } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import { z } from 'zod'

import {
  TMapZoomLevel,
  ZMapZoomLevel,
  ZSearchFilter,
  type TMapParams,
} from '@/entities/map'
import {
  MAP_EVENT_TYPE,
  TCoordinates,
  useMapBridge,
  ZCoordinates,
} from '@/shared/lib'

const ZRoomMarker = z.object({
  lat: z.number().nullable(),
  lng: z.number().nullable(),
})

const ZFilterOptions = ZSearchFilter.omit({
  startDate: true,
  endDate: true,
}).partial()

const ZFilter = ZSearchFilter.pick({
  startDate: true,
  endDate: true,
})
  .merge(ZCoordinates)
  .extend({
    zoomLevel: ZMapZoomLevel.optional(),
    filter: ZFilterOptions.optional(),
  })
  .optional()

export type TRoomMarker = z.infer<typeof ZRoomMarker>
export type TFilter = z.infer<typeof ZFilter>
export type TFilterOptions = z.infer<typeof ZFilterOptions>

const SEARCH_MAP_EVENT_TYPE = {
  ...MAP_EVENT_TYPE,
  VIEWPORT: 'map.viewport',
  ROOM_MARKER: 'map.roomMarker',
  SEARCH: 'map.search',
}

const convertToFilter = (
  filterProp: TFilter,
): {
  coordinates?: TCoordinates
  zoomLevel?: TMapZoomLevel
  startDate?: string
  endDate?: string
  options?: TFilterOptions
} => {
  const { lat, lng, zoomLevel, startDate, endDate, filter } = filterProp ?? {}

  const hasCoordinates = !(isNil(lat) || isNil(lng))

  const hasStartDate = !isNil(startDate)
  const hasEndDate = !isNil(endDate)
  const hasZoomLevel = !isNil(zoomLevel)
  const hasFilter = !isNil(filter) && !isEmpty(filter)

  return {
    ...(hasCoordinates && { coordinates: { lat, lng } }),
    ...(hasZoomLevel && { zoomLevel }),
    ...(hasStartDate && { startDate: startDate }),
    ...(hasEndDate && { endDate }),
    options: {
      ...(hasFilter && omitBy(filter, isNil)),
    },
  }
}

export const useSearchMapBridge = () => {
  const { bridge, mapBridge } = useMapBridge()

  const updateViewport = useCallback(
    (viewport: TMapParams) => {
      if (!bridge) return

      bridge.postMessage({
        type: SEARCH_MAP_EVENT_TYPE.VIEWPORT,
        payload: viewport,
      })
    },
    [bridge],
  )

  const updateRoomMarker = useCallback(
    (roomMarker: TRoomMarker) => {
      if (!bridge) return

      bridge.postMessage({
        type: SEARCH_MAP_EVENT_TYPE.ROOM_MARKER,
        payload: {
          roomMarkerLat: roomMarker.lat,
          roomMarkerLng: roomMarker.lng,
        },
      })
    },
    [bridge],
  )

  const onFilterChange = useCallback(
    (
      callback: (filter: {
        coordinates?: TCoordinates
        zoomLevel?: TMapZoomLevel
        startDate?: string
        endDate?: string
        options?: TFilterOptions
      }) => void,
    ): (() => void) => {
      if (!bridge) return () => {}

      const unsubscribe = bridge.addEventListener(
        SEARCH_MAP_EVENT_TYPE.SEARCH,
        payload => {
          callback(convertToFilter(payload as TFilter))
        },
      )

      return unsubscribe
    },
    [bridge],
  )

  const searchMapBridge = useMemo(
    () => ({
      ...mapBridge,
      updateViewport,
      updateRoomMarker,
      onFilterChange,
    }),
    [mapBridge, updateViewport, updateRoomMarker, onFilterChange],
  )

  return searchMapBridge
}
